import { loader } from 'graphql.macro';

import { appClient } from 'src/config/apollo';
import i18n from 'src/config/i18n';
import { MeditationProgressNode } from 'src/generated/webapp_gql';
import { ToastNotificationsService } from 'src/shared/services/ToastNotificationsService';

import { MeditationCategoryWithMeditations } from '../models/meditation';

const GET_MEDITATION_CATEGORIES_QUERY = loader('../documents/getMeditationCategoriesQuery.graphql');
const GET_MEDITATION_PROGRESS_QUERY = loader('../documents/getMeditationProgressQuery.graphql');
const START_MEDITATION_MUTATION = loader('../documents/startMeditationMutation.graphql');
const FINISH_MEDITATION_MUTATION = loader('../documents/finishMeditationMutation.graphql');

export class MeditationService {
    constructor(private toastNotificationsService: ToastNotificationsService) {}

    public async fetchMeditationCategories(): Promise<MeditationCategoryWithMeditations[]> {
        let meditationCategoryData: MeditationCategoryWithMeditations[] = [];

        const lang = i18n.language;

        try {
            const meditationCategoryQueryResult = await appClient.query({
                query: GET_MEDITATION_CATEGORIES_QUERY,
                variables: {
                    languageCode: lang,
                },
                fetchPolicy: 'network-only',
            });
            meditationCategoryData = meditationCategoryQueryResult.data.meditationCategories;
        } catch (error) {
            this.toastNotificationsService.renderErrorToast('Could not fetch meditations.');
        }

        if (!meditationCategoryData || !meditationCategoryData.length) {
            return [];
        }

        return meditationCategoryData.filter(category => category.meditations.length > 0);
    }

    public async fetchMeditationProgress(): Promise<MeditationProgressNode[]> {
        return appClient
            .query({
                query: GET_MEDITATION_PROGRESS_QUERY,
                fetchPolicy: 'network-only',
            })
            .then(result => result.data.meditationProgress)
            .catch(() =>
                this.toastNotificationsService.renderErrorToast('Could not fetch meditations.'),
            );
    }

    public async startMeditation(meditationName: string): Promise<MeditationProgressNode[]> {
        return appClient
            .mutate({
                mutation: START_MEDITATION_MUTATION,
                variables: { meditationName },
            })
            .then(result => result.data.meditationProgress)
            .catch(() => null);
    }

    public async finishMeditation(meditationName: string): Promise<MeditationProgressNode[]> {
        return appClient
            .mutate({
                mutation: FINISH_MEDITATION_MUTATION,
                variables: { meditationName },
            })
            .then(result => result.data.meditationProgress)
            .catch(() => null);
    }
}
